/**
 * storeSiteConfig.ts — Store/Catalog Template Configuration
 *
 * This config defines the store template's categories, products,
 * navigation, and layout preferences. Used when templateType === "store".
 *
 * E-commerce ready: products have fixed prices, variations, stock status,
 * and sale badges.
 */

/* ─────────────────────────────────────────────
   1. BUSINESS IDENTITY
   ───────────────────────────────────────────── */

export const identity = {
  name: "STORE NAME",
  nameHebrew: "שם החנות",
  tagline: "ייצור ושיווק מוצרים מקצועיים",
  description:
    "חנות מקוונת למוצרים מקצועיים ואיכותיים. עוד רב על כל המוצרים והשירותים שלנו באתר.",
  copyright: `\u00A9 ${new Date().getFullYear()} STORE NAME - שם החנות`,
  rightsReserved: "כל הזכויות שמורות",
};

/* ─────────────────────────────────────────────
   2. CONTACT DETAILS
   ───────────────────────────────────────────── */

export const contact = {
  phone: "073-000-0000",
  phoneHref: "tel:0730000000",
  mobile: "050-000-0000",
  mobileHref: "tel:0500000000",
  whatsappUrl: "https://wa.me/972500000000",
  email: "info@store-example.co.il",
  emailHref: "mailto:info@store-example.co.il",
  address: "רחוב התעשייה 3, אזור התעשייה, תל אביב",
  established: "ייצור בישראל מאז 1945",
};

/* ─────────────────────────────────────────────
   3. BUSINESS HOURS
   ───────────────────────────────────────────── */

export const hours = [
  { days: "א׳-ד׳", time: "08:00-19:00" },
  { days: "ו׳", time: "08:00-13:00" },
];

/* ─────────────────────────────────────────────
   4. NAVIGATION
   ───────────────────────────────────────────── */

export interface NavItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
}

export const navigation: NavItem[] = [
  { label: "ראשי", path: "/" },
  { label: "אודות", path: "/about" },
  { label: "קטלוג מוצרים", path: "/catalog" },
  { label: "בלוג המוצרים", path: "/blog" },
  { label: "צרו קשר", path: "/contact" },
];

/* ─────────────────────────────────────────────
   5. FOOTER LINKS
   ───────────────────────────────────────────── */

export const footerQuickLinks = [
  { label: "דף הבית", path: "/" },
  { label: "אודות", path: "/about" },
  { label: "בלוג", path: "/blog" },
  { label: "צרו קשר", path: "/contact" },
];

export const footerCategoryLinks: { label: string; path: string }[] = [];

/* ─────────────────────────────────────────────
   6. BREADCRUMBS
   ───────────────────────────────────────────── */

export const breadcrumbs: Record<string, { label: string; path?: string }[]> = {
  "/about": [{ label: "אודות" }],
  "/catalog": [{ label: "קטלוג מוצרים" }],
  "/blog": [{ label: "בלוג" }],
  "/contact": [{ label: "צרו קשר" }],
  "/seo-manager": [{ label: "ניהול SEO" }],
  "/change-history": [{ label: "היסטוריית שינויים" }],
  "/business-info": [{ label: "רקע על העסק" }],
  "/qa-checklist": [{ label: "צ׳קליסט QA" }],
};

/* ─────────────────────────────────────────────
   7. LAYOUT PREFERENCES
   ───────────────────────────────────────────── */

export const layout = {
  hideContactFormOn: ["/contact", "/seo-manager", "/change-history", "/business-info", "/site-tree", "/qa-checklist"],
  homePath: "/",
  fontFamily: "var(--font-family)",
  homeBreadcrumbLabel: "דף הבית",
};

/* ─────────────────────────────────────────────
   8. CONTACT FORM
   ───────────────────────────────────────────── */

export const contactForm = {
  title: "צרו קשר",
  subtitle: "אנחנו כאן לכל שאלה. השאירו פרטים ונחזור אליכם בהקדם",
  submitLabel: "שליחה",
  fields: {
    namePlaceholder: "שם מלא",
    phonePlaceholder: "טלפון",
    emailPlaceholder: "אימייל",
    messagePlaceholder: "הודעה",
  },
};

/* ─────────────────────────────────────────────
   9. CTA DEFAULTS
   ───────────────────────────────────────────── */

export const ctaDefaults = {
  primaryLabel: "לצפייה בקטלוג",
  primaryLink: "/catalog",
  contactLabel: "צרו קשר",
  contactLink: "/contact",
};

/* ─────────────────────────────────────────────
   10. STORE CATEGORIES
   ───────────────────────────────────────────── */

export interface StoreCategory {
  slug: string;
  name: string;
  description: string;
  longDescription?: string;
  advantages?: string[];
  faq?: { q: string; a: string }[];
}

export const categories: StoreCategory[] = [
  {
    slug: "category-1",
    name: "קטגוריה ראשונה",
    description: "תיאור קצר של קטגוריה ראשונה נכתב כללית",
    longDescription: "הקטגוריה הראשונה של החנות. כאן יופיע מידע על כל המוצרים בקטגוריה זו, עם מידע מקצועי ומחירים תחרותיים. אנחנו מציעים מגוון רחב של מוצרים איכותיים לכל צורך, בין אם הדבר קשור לשימוש פרטי או עסקי.",
    advantages: [
      "איכות מעולה ומדויקת לאורך שנים",
      "מגוון רחב של דגמים וגדלים",
      "התאמות מוצרים לדרישות הייחודיות של כל לקוח",
    ],
    faq: [
      { q: "כמה עולה לייצר קטגוריה ראשונה בהתאמה?", a: "המחיר משתנה בהתאם לדרישות הלקוח." },
      { q: "כמה זמן לוקח לייצר קטגוריה ראשונה בהזמנה?", a: "זמן הייצור תלוי בהיקף ההזמנה והמורכבות." },
      { q: "האם אפשר לייצר מוצרים בהתאמה קטנה ועל?", a: "בוודאי! אנחנו מתמחים בהתאמות אישיות." },
      { q: "איך נראית של קטגוריה ראשונה בשטח?", a: "המוצרים שלנו מתאימים לשימוש במגוון סביבות." },
    ],
  },
  {
    slug: "category-2",
    name: "קטגוריה שנייה",
    description: "תיאור קצר של קטגוריה שנייה נכתב כללית",
    advantages: ["טכנולוגיה מתקדמת", "אחריות מורחבת", "תמיכה טכנית מלאה"],
    faq: [
      { q: "כמה עולה לייצר קטגוריה שנייה בהתאמה?", a: "המחיר משתנה בהתאם לדרישות." },
    ],
  },
  {
    slug: "category-3",
    name: "קטגוריה שלישית",
    description: "תיאור קצר של קטגוריה שלישית נכתב כללית",
    advantages: ["עיצוב מודרני", "עמידות גבוהה", "התקנה פשוטה"],
  },
  {
    slug: "category-4",
    name: "קטגוריה רביעית",
    description: "תיאור קצר של קטגוריה רביעית נכתב כללית",
  },
];

/* ─────────────────────────────────────────────
   11. PRODUCT VARIATIONS & TYPES
   ───────────────────────────────────────────── */

export interface ProductVariationOption {
  value: string;
  label: string;
  colorHex?: string;
}

export interface ProductVariation {
  type: string;
  label: string;
  options: ProductVariationOption[];
}

/* ─────────────────────────────────────────────
   12. STORE PRODUCTS
   ───────────────────────────────────────────── */

export interface StoreProduct {
  slug: string;
  name: string;
  categorySlug: string;
  shortDescription: string;
  fullDescription: string;
  /** Fixed numeric price in ILS */
  price: number;
  /** Original price before sale (shown struck-through) */
  originalPrice?: number;
  /** Legacy price range string — kept for backward compat */
  priceRange?: string;
  /** Product variations (color, size, material, etc.) */
  variations?: ProductVariation[];
  /** Stock availability */
  inStock?: boolean;
  /** Badge label shown on product card */
  badge?: "sale" | "new" | "bestseller";
  /** SKU code */
  sku?: string;
  features?: string[];
  targetAudience?: string;
  specs?: { label: string; value: string }[];
  /** True for auto-generated placeholder slots (product-13 through product-100) */
  placeholder?: boolean;
}

/** Badge display labels */
export const badgeLabels: Record<string, { text: string; bgClass: string }> = {
  sale: { text: "מבצע", bgClass: "bg-red-500" },
  new: { text: "חדש", bgClass: "bg-emerald-500" },
  bestseller: { text: "נמכר ביותר", bgClass: "bg-amber-500" },
};

/* ── Shared variation sets ── */

const COLOR_VARIATIONS: ProductVariation = {
  type: "color",
  label: "צבע",
  options: [
    { value: "black", label: "שחור", colorHex: "#1a1a1a" },
    { value: "white", label: "לבן", colorHex: "#f5f5f5" },
    { value: "blue", label: "כחול", colorHex: "#2563eb" },
    { value: "red", label: "אדום", colorHex: "#dc2626" },
  ],
};

const SIZE_VARIATIONS: ProductVariation = {
  type: "size",
  label: "מידה",
  options: [
    { value: "S", label: "S" },
    { value: "M", label: "M" },
    { value: "L", label: "L" },
    { value: "XL", label: "XL" },
  ],
};

const MATERIAL_VARIATIONS: ProductVariation = {
  type: "material",
  label: "חומר",
  options: [
    { value: "standard", label: "רגיל" },
    { value: "premium", label: "פרימיום" },
    { value: "eco", label: "אקולוגי" },
  ],
};

export const products: StoreProduct[] = [
  {
    slug: "product-1",
    name: "מוצר ראשון",
    categorySlug: "category-1",
    shortDescription: "תיאור קצר של המוצר הראשון",
    fullDescription: "תיאור מפורט של המוצר הראשון. כתבו כאן מידע מקיף על המוצר — מה הוא, למי הוא מתאים, מה היתרונות שלו, ואיך הוא פותר בעיה ללקוח. כתבו לפחות 3-4 משפטים.",
    price: 199,
    originalPrice: 249,
    badge: "sale",
    inStock: true,
    sku: "PRD-001",
    variations: [COLOR_VARIATIONS, SIZE_VARIATIONS],
    features: [
      "מאפיין 1",
      "מאפיין 2",
      "מאפיין 3",
      "מאפיין 4",
      "מאפיין 5",
    ],
    targetAudience: "תיאור קהל היעד של המוצר",
    specs: [
      { label: "שדה 1", value: "ערך 1" },
      { label: "שדה 2", value: "ערך 2" },
      { label: "שדה 3", value: "ערך 3" },
    ],
  },
  {
    slug: "product-2",
    name: "מוצר שני",
    categorySlug: "category-1",
    shortDescription: "תיאור קצר של המוצר השני",
    fullDescription: "תיאור מפורט של המוצר השני.",
    price: 140,
    inStock: true,
    sku: "PRD-002",
    variations: [COLOR_VARIATIONS],
    features: ["תכונה א׳", "תכונה ב׳"],
  },
  {
    slug: "product-3",
    name: "מוצר שלישי",
    categorySlug: "category-1",
    shortDescription: "תיאור קצר של המוצר השלישי",
    fullDescription: "תיאור מפורט של המוצר השלישי.",
    price: 200,
    originalPrice: 260,
    badge: "sale",
    inStock: true,
    sku: "PRD-003",
    variations: [SIZE_VARIATIONS],
  },
  {
    slug: "product-4",
    name: "מוצר רביעי",
    categorySlug: "category-2",
    shortDescription: "תיאור קצר של המוצר הרביעי",
    fullDescription: "תיאור מפורט של המוצר הרביעי.",
    price: 250,
    inStock: true,
    sku: "PRD-004",
    variations: [COLOR_VARIATIONS],
  },
  {
    slug: "product-5",
    name: "מוצר חמישי",
    categorySlug: "category-2",
    shortDescription: "תיאור קצר של המוצר החמישי",
    fullDescription: "תיאור מפורט של המוצר החמישי.",
    price: 180,
    originalPrice: 220,
    badge: "new",
    inStock: true,
    sku: "PRD-005",
    variations: [COLOR_VARIATIONS, SIZE_VARIATIONS],
  },
  {
    slug: "product-6",
    name: "מוצר שישי",
    categorySlug: "category-2",
    shortDescription: "תיאור קצר של המוצר השישי",
    fullDescription: "תיאור מפורט של המוצר השישי.",
    price: 300,
    inStock: false,
    sku: "PRD-006",
    variations: [COLOR_VARIATIONS],
  },
  {
    slug: "product-7",
    name: "מוצר שביעי",
    categorySlug: "category-3",
    shortDescription: "תיאור קצר של המוצר השביעי",
    fullDescription: "תיאור מפורט של המוצר השביעי.",
    price: 220,
    inStock: true,
    sku: "PRD-007",
    variations: [SIZE_VARIATIONS],
  },
  {
    slug: "product-8",
    name: "מוצר שמיני",
    categorySlug: "category-3",
    shortDescription: "תיאור קצר של המוצר השמיני",
    fullDescription: "תיאור מפורט של המוצר השמיני.",
    price: 160,
    originalPrice: 199,
    badge: "sale",
    inStock: true,
    sku: "PRD-008",
    variations: [MATERIAL_VARIATIONS],
  },
  {
    slug: "product-9",
    name: "מוצר תשיעי",
    categorySlug: "category-3",
    shortDescription: "תיאור קצר של המוצר התשיעי",
    fullDescription: "תיאור מפורט של המוצר התשיעי.",
    price: 190,
    inStock: true,
    sku: "PRD-009",
    variations: [SIZE_VARIATIONS],
  },
  {
    slug: "product-10",
    name: "מוצר עשירי",
    categorySlug: "category-4",
    shortDescription: "תיאור קצר של המוצר העשירי",
    fullDescription: "תיאור מפורט של המוצר העשירי.",
    price: 280,
    inStock: true,
    sku: "PRD-010",
    variations: [COLOR_VARIATIONS, MATERIAL_VARIATIONS],
  },
  {
    slug: "product-11",
    name: "מוצר אחד-עשר",
    categorySlug: "category-4",
    shortDescription: "תיאור קצר של המוצר האחד-עשר",
    fullDescription: "תיאור מפורט של המוצר האחד-עשר.",
    price: 350,
    originalPrice: 420,
    badge: "bestseller",
    inStock: true,
    sku: "PRD-011",
    variations: [COLOR_VARIATIONS, SIZE_VARIATIONS],
  },
  {
    slug: "product-12",
    name: "מוצר שנים-עשר",
    categorySlug: "category-4",
    shortDescription: "תיאור קצר של המוצר השנים-עשר",
    fullDescription: "תיאור מפורט של המוצר השנים-עשר.",
    price: 400,
    inStock: true,
    sku: "PRD-012",
    variations: [MATERIAL_VARIATIONS],
  },
  /* ── Placeholder slots for imported products (13-100) ── */
  ...generatePlaceholderProducts(13, 100),
];

/** Generate placeholder product slots for import support (up to 100 products) */
function generatePlaceholderProducts(from: number, to: number): StoreProduct[] {
  const result: StoreProduct[] = [];
  const cats = ["category-1", "category-2", "category-3", "category-4"];
  for (let i = from; i <= to; i++) {
    result.push({
      slug: `product-${i}`,
      name: `\u05DE\u05D5\u05E6\u05E8 ${i}`,
      categorySlug: cats[(i - 1) % cats.length],
      shortDescription: "",
      fullDescription: "",
      price: 0,
      inStock: true,
      sku: `PRD-${String(i).padStart(3, "0")}`,
      placeholder: true,
    });
  }
  return result;
}

/** Number of original (non-placeholder) products */
export const ORIGINAL_PRODUCT_COUNT = 12;

/** Maximum number of products supported (including placeholders) */
export const MAX_PRODUCT_COUNT = 100;

/* ─────────────────────────────────────────────
   13. ABOUT & SERVICES DATA
   ───────────────────────────────────────────── */

export const aboutText = {
  title: "אודות",
  paragraph1:
    "אנחנו חברה מובילה, חונם ייצור ושיווק מוצרים מקצועיים. התקוום בשנת 1945 עם חזון משותף — לספק ללקוחות מוצרים מקצועיים, במחירים הוגנים, ועם שירות אישי המקפיד.",
  paragraph2:
    "במרכז העניינים בינו מחפשים של איכות, אריכות ושירות. המפעל שלנו מייד תהליך מקצועי.",
  paragraph3:
    "אם אתם מחפשים התאמה אישית של מוצרים לצרכים הייחודיים של כל לקוח, בין אם הדבר קשור לייצור עצמי, הזמנה גדולה, או לקיחת מוצר מדף — אנחנו כאן כדי לייעד לכם להגיע את המוצר המושלם.",
};

export const additionalServices = [
  {
    icon: "design",
    title: "עיצוב גרפי",
    description: "שירותי עיצוב גרפי להתאמה לצורך הלקוח שלכם. עיצוב האריזות ואמבלאז׳ים למוצרים.",
  },
  {
    icon: "customize",
    title: "ייצור בהתאמה אישית",
    description: "ייצור מוצרים בהתאמה מלאה לדרישות הלקוח — גודל, צבע, חומר, ועוד.",
  },
  {
    icon: "shipping",
    title: "משלוחים לכל הארץ",
    description: "שירותי משלוחים מהירים ואמינים לכל רחבי הארץ. אפשרות לאיסוף עצמי מהמפעל.",
  },
];

/* ─────────────────────────────────────────────
   14. ADVANTAGES (HOME PAGE)
   ───────────────────────────────────────────── */

export const advantages = [
  { icon: "award", title: "איכות מעולה", desc: "מוצרים איכותיים בסטנדרטים הגבוהים ביותר" },
  { icon: "dollar", title: "תחקוק מחיר אונליין", desc: "בדקו את המחירים שלנו — אתם תהיו מופתעים" },
  { icon: "truck", title: "שילוח מהיר", desc: "משלוחים לכל הארץ בזמן קצר" },
  { icon: "factory", title: "ללא עמלות תיווך", desc: "מוצרים ישירות מהמקור ללא תיווך" },
];

/* ─────────────────────────────────────────────
   15. PROMOTIONAL STRIP
   ───────────────────────────────────────────── */

export const promoStrip = {
  title: "משלוח חינם בהזמנה מעל 300 שקלים!",
  subtitle: "הירשמו לניוזלטר וקבלו 10% הנחה על ההזמנה הראשונה",
  ctaLabel: "להירשם עכשיו",
  freeShippingThreshold: 300,
};

/* ─────────────────────────────────────────────
   16. HELPER FUNCTIONS
   ───────────────────────────────────────────── */

export function getProductsByCategory(categorySlug: string): StoreProduct[] {
  return products.filter((p) => p.categorySlug === categorySlug);
}

export function getProduct(slug: string): StoreProduct | undefined {
  return products.find((p) => p.slug === slug);
}

export function getCategory(slug: string): StoreCategory | undefined {
  return categories.find((c) => c.slug === slug);
}

export function getRelatedProducts(currentSlug: string, limit = 4): StoreProduct[] {
  const current = getProduct(currentSlug);
  const nonPlaceholder = products.filter((p) => !p.placeholder);
  if (!current) return nonPlaceholder.slice(0, limit);
  return nonPlaceholder
    .filter((p) => p.slug !== currentSlug && p.categorySlug === current.categorySlug)
    .slice(0, limit);
}

export function getAllProducts(): StoreProduct[] {
  return products;
}

/** Format price to ILS string */
export function formatPrice(price: number): string {
  return `\u20AA${price.toLocaleString("he-IL")}`;
}